// ═══════════════════════════════════════════════════════════════
// PdfTicketViewer — Client-side PDF viewer using PDF.js
// Loads a PDF via proxy URL, renders pages on canvas, and
// auto-searches for a ticket number to jump to that page.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Stack, Text, Button, Group, Badge } from '../gab/GabComponents';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

// Configure worker once at module load — Vite resolves this to a bundled asset URL
if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).href;
}

interface Props {
  url: string;
  searchText?: string;
  initialPage?: number;   // jump directly to this page instead of scanning
  title?: string;
  onClose?: () => void;
}

export default function PdfTicketViewer({ url, searchText, initialPage, title, onClose }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdf, setPdf] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [matchedPage, setMatchedPage] = useState<number | null>(null);
  const [searchStatus, setSearchStatus] = useState('');
  const renderTaskRef = useRef<number>(0);

  // Load PDF
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError('');
        setSearchStatus('');
        setMatchedPage(null);

        // Fetch the full PDF binary upfront — passing ArrayBuffer to PDF.js
        // avoids streaming/range-request issues that cause images to not render.
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status} fetching PDF`);
        const arrayBuffer = await resp.arrayBuffer();
        if (cancelled) return;

        const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
        const pdfDoc = await loadingTask.promise;

        if (cancelled) return;
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);

        // If an exact page number is known, jump directly — no scanning needed
        if (initialPage && initialPage >= 1 && initialPage <= pdfDoc.numPages) {
          setCurrentPage(initialPage);
          setMatchedPage(initialPage);
          setSearchStatus(`Page ${initialPage} of ${pdfDoc.numPages}`);
        } else if (searchText) {
          // Fall back to scanning when no page number is available
          setSearchStatus(`Searching for "${searchText}"...`);
          let found = false;
          for (let i = 1; i <= pdfDoc.numPages; i++) {
            const page = await pdfDoc.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item: any) => item.str).join(' ');
            if (pageText.includes(searchText)) {
              if (cancelled) return;
              setMatchedPage(i);
              setCurrentPage(i);
              setSearchStatus(`Found on page ${i} of ${pdfDoc.numPages}`);
              found = true;
              break;
            }
            if (i % 10 === 0 && !cancelled) {
              setSearchStatus(`Searching... page ${i}/${pdfDoc.numPages}`);
            }
          }
          if (!found && !cancelled) {
            setSearchStatus(`"${searchText}" not found in ${pdfDoc.numPages} pages`);
            setCurrentPage(1);
          }
        } else {
          setCurrentPage(1);
        }
      } catch (e) {
        if (!cancelled) setError(`Failed to load PDF: ${e}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [url, searchText, initialPage]);

  // Render current page
  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    const taskId = ++renderTaskRef.current;

    (async () => {
      try {
        const page = await pdf.getPage(currentPage);
        if (taskId !== renderTaskRef.current) return;

        const containerWidth = containerRef.current?.clientWidth ?? 800;
        const unscaledViewport = page.getViewport({ scale: 1 });
        const fitScale = Math.min(1.5, (containerWidth - 32) / unscaledViewport.width);
        const viewport = page.getViewport({ scale: fitScale });

        const canvas = canvasRef.current!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport }).promise;
      } catch (e) {
        console.error('Render page error:', e);
      }
    })();
  }, [pdf, currentPage]);

  if (error) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Text c="red">{error}</Text>
      </div>
    );
  }

  return (
    <Stack gap={0} style={{ height: '100%' }}>
      <Group gap="sm" p="sm" style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0, flexWrap: 'wrap' }}>
        <Button size="xs" variant="subtle" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1 || loading}>
          <IconChevronLeft size={16} />
        </Button>
        <Text size="sm" fw={600}>
          {loading ? 'Loading...' : `Page ${currentPage} of ${totalPages}`}
        </Text>
        <Button size="xs" variant="subtle" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages || loading}>
          <IconChevronRight size={16} />
        </Button>
        {searchStatus && (
          <Badge size="sm" color={matchedPage ? 'var(--color-success)' : '#94a3b8'} variant="light">
            {searchStatus}
          </Badge>
        )}
      </Group>

      <div
        ref={containerRef}
        style={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', padding: 16, background: '#525659' }}
      >
        {loading && !pdf ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Text c="white" size="lg">Loading PDF...</Text>
          </div>
        ) : (
          <canvas ref={canvasRef} style={{ maxWidth: '100%', boxShadow: '0 2px 10px rgba(0,0,0,0.3)' }} />
        )}
      </div>
    </Stack>
  );
}
